import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Section « Consultés récemment » — UI miroir « Produits recommandés ».
 * Hors produit / famille Combined Listing courants.
 * Un seul coloris par modèle. Masquée s’il n’y a rien à montrer.
 */
class RecentlyViewedSection extends HTMLElement {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    if (this.dataset.loaded === 'true') return;

    if (Shopify.designMode && this.querySelector('[data-has-recommendations="true"]')) {
      this.dataset.loaded = 'true';
      return;
    }

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  async #load() {
    if (this.dataset.loaded === 'true') return;

    try {
      const ids = this.#resolveProductIds();
      if (!ids.length) {
        this.classList.add('hidden');
        this.dataset.loaded = 'true';
        this.dataset.hasRecommendations = 'false';
        return;
      }

      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;

      const url = new URL(Theme.routes.search_url || '/search', location.origin);
      url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('type', 'product');

      this.#activeFetch?.abort();
      this.#activeFetch = new AbortController();

      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector(`recently-viewed-products[data-section-id="${sectionId}"]`);

      if (next?.innerHTML?.trim() && next.querySelector('[data-has-recommendations="true"]')) {
        this.innerHTML = next.innerHTML;
        this.dataset.loaded = 'true';
        this.dataset.hasRecommendations = 'true';
        this.classList.remove('hidden');
      } else {
        this.classList.add('hidden');
        this.dataset.loaded = 'true';
        this.dataset.hasRecommendations = 'false';
      }
    } catch (error) {
      console.error('Recently viewed section error:', error);
      this.classList.add('hidden');
      this.dataset.loaded = 'true';
      this.dataset.hasRecommendations = 'false';
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * @returns {string[]}
   */
  #resolveProductIds() {
    const limit = Number(this.dataset.maxProducts) || 4;
    const currentId = String(this.dataset.productId || '').trim();
    const currentFamily = String(this.dataset.familyKey || currentId || '').trim();
    const entries = RecentlyViewed.getEntries();

    /** @type {string[]} */
    const ids = [];
    const seenFamilies = new Set();

    for (const entry of entries) {
      if (!entry?.id) continue;
      if (currentId && entry.id === currentId) continue;
      if (currentFamily && entry.familyKey === currentFamily) continue;
      if (seenFamilies.has(entry.familyKey)) continue;

      seenFamilies.add(entry.familyKey);
      ids.push(entry.id);
      if (ids.length >= limit) break;
    }

    return ids;
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedSection);
}
